import React from "react";
import { Monitor, MonitorOff, ListMusic, ListPlus, QrCode, Inbox, Maximize2, Download, BarChart3, Library } from "lucide-react";
import { useKaraoke } from "../../store/KaraokeStore";
import { toast } from "sonner";

const TopBar = ({ onOpenPlaylists, onSavePlaylist, onOpenPending, onOpenStats, onOpenLibrary }) => {
  const {
    secondScreenOpen,
    toggleSecondScreen,
    requestSecondFullscreen,
    queue,
    pendingSubmissions,
    qrActive,
    toggleQr,
    downloadHistory,
  } = useKaraoke();
  const pendingCount = pendingSubmissions.length;

  const handleDownloadHistory = async () => {
    try {
      const count = await downloadHistory();
      toast.success("Historial descargado", {
        description: `${count} canción(es) exportadas a un archivo de texto.`,
      });
    } catch (e) {
      console.error("Failed to download history", e);
      toast.error("No se pudo descargar el historial");
    }
  };

  const handleToggleQr = () => {
    const willActivate = !qrActive;
    toggleQr();
    if (willActivate) {
      if (secondScreenOpen) {
        toast.success("QR activado en la 2ª pantalla");
      } else {
        toast("QR activado", {
          description: "Abre la 2ª pantalla para mostrar el QR a tus invitados.",
        });
      }
    }
  };

  return (
    <header
      className="h-16 border-b border-border bg-surface flex items-center justify-between px-6 shrink-0 relative z-20"
      data-testid="top-bar"
    >
      <div className="flex items-center gap-3">
        <img src="/logo-dj-gabo.png" alt="DJ Gabo Karaoke" data-testid="console-header-logo" className="h-11 w-auto object-contain shrink-0" />
        <div>
          <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-muted-foreground">
            Powered by YouTube
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={onOpenPending}
          data-testid="open-pending-button"
          className="relative flex items-center gap-2 px-3 h-9 rounded-md border border-border hover:border-secondary hover:text-secondary transition-colors text-sm font-medium"
          title="Solicitudes de invitados"
        >
          <Inbox className="w-4 h-4" />
          <span className="hidden md:inline">Pendientes</span>
          {pendingCount > 0 && (
            <span
              data-testid="pending-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center glow-pink"
            >
              {pendingCount}
            </span>
          )}
        </button>

        <button
          onClick={handleToggleQr}
          data-testid="open-qr-button"
          className={`flex items-center gap-2 px-3 h-9 rounded-md border text-sm font-medium transition-all ${
            qrActive
              ? "border-secondary bg-secondary/10 text-secondary glow-cyan-border"
              : "border-border hover:border-secondary hover:text-secondary"
          }`}
        >
          <QrCode className="w-4 h-4" />
          <span className="hidden md:inline">{qrActive ? "Cerrar QR" : "QR Invitados"}</span>
        </button>

        <button
          onClick={onSavePlaylist}
          disabled={queue.length === 0}
          data-testid="save-playlist-button"
          className="hidden lg:flex items-center gap-2 px-3 h-9 rounded-md border border-border hover:border-secondary hover:text-secondary transition-colors text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ListPlus className="w-4 h-4" />
          Guardar
        </button>

        <button
          onClick={onOpenPlaylists}
          data-testid="open-playlists-button"
          className="flex items-center gap-2 px-3 h-9 rounded-md border border-border hover:border-secondary hover:text-secondary transition-colors text-sm font-medium"
        >
          <ListMusic className="w-4 h-4" />
          <span className="hidden md:inline">Listas</span>
        </button>

        <button
          onClick={handleDownloadHistory}
          data-testid="download-history-button"
          className="flex items-center gap-2 px-3 h-9 rounded-md border border-border hover:border-secondary hover:text-secondary transition-colors text-sm font-medium"
          title="Descargar historial de canciones (.txt)"
        >
          <Download className="w-4 h-4" />
          <span className="hidden lg:inline">Historial</span>
        </button>

        <button
          onClick={onOpenLibrary}
          data-testid="open-library-button"
          className="flex items-center gap-2 px-3 h-9 rounded-md border border-border hover:border-secondary hover:text-secondary transition-colors text-sm font-medium"
          title="Biblioteca, favoritos y caché"
        >
          <Library className="w-4 h-4" />
          <span className="hidden lg:inline">Biblioteca</span>
        </button>

        <button
          onClick={onOpenStats}
          data-testid="open-stats-button"
          className="flex items-center justify-center w-9 h-9 rounded-md border border-border hover:border-secondary hover:text-secondary transition-colors"
          title="Cuota de YouTube"
        >
          <BarChart3 className="w-4 h-4" />
        </button>

        <button
          onClick={toggleSecondScreen}
          data-testid="second-screen-toggle"
          className={`flex items-center gap-2 px-4 h-9 rounded-md text-sm font-bold uppercase tracking-wide transition-all ${
            secondScreenOpen
              ? "bg-secondary text-secondary-foreground glow-cyan-border"
              : "bg-transparent border border-border hover:border-secondary hover:text-secondary"
          }`}
        >
          {secondScreenOpen ? <MonitorOff className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
          {secondScreenOpen ? "Cerrar 2ª" : "2ª Pantalla"}
        </button>

        {secondScreenOpen && (
          <button
            onClick={requestSecondFullscreen}
            data-testid="request-fullscreen-button"
            className="flex items-center gap-2 px-3 h-9 rounded-md border border-secondary/40 text-secondary hover:bg-secondary/10 transition-colors text-sm font-medium"
            title="Pantalla completa en 2ª pantalla"
          >
            <Maximize2 className="w-4 h-4" />
            <span className="hidden xl:inline">Pantalla Completa</span>
          </button>
        )}
      </div>
    </header>
  );
};

export default TopBar;
